import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { Save as SaveIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { HistoryEdu as ApplyIcon } from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import { useSeason } from "../contexts/SeasonContext";
import SeasonSelector from "../components/SeasonSelector";

interface PointsConfig {
  levels: {
    [level: string]: {
      [position: number]: number;
    };
  };
  lastUpdated: string | null;
  updatedBy?: string;
}

function TournamentPointsConfig() {
  const { currentOrg } = useOrganization();
  const { selectedSeason } = useSeason();
  const [config, setConfig] = useState<PointsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [generatingStandings, setGeneratingStandings] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [generateStandingsDialogOpen, setGenerateStandingsDialogOpen] = useState(false);

  // Get organization divisions or use default levels
  const levels = currentOrg?.divisions || ["Open", "Advanced", "Intermediate", "Beginner"];
  const positions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]; // Master Cup (1-8) + Tour Cup (9-16)

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");

      const response = await apiRequest("/api/tournament-settings/points-config", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      setConfig(data);
    } catch (err: any) {
      setError("Failed to load points configuration");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePointsChange = (level: string, position: number, value: string) => {
    if (!config) return;

    const numValue = parseInt(value) || 0;
    setConfig({
      ...config,
      levels: {
        ...config.levels,
        [level]: {
          ...config.levels[level],
          [position]: numValue,
        },
      },
    });
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("authToken");

      await apiRequest("/api/tournament-settings/points-config", {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ levels: config.levels }),
      });

      setSuccess("Points configuration saved successfully!");
      fetchConfig(); // Refresh to get updated timestamp
    } catch (err: any) {
      setError("Failed to save points configuration");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setConfig({
      levels: {
        Open: { 1: 100, 2: 70, 3: 50, 4: 35, 5: 25, 6: 20, 7: 15, 8: 10 },
        Advanced: { 1: 80, 2: 56, 3: 40, 4: 28, 5: 20, 6: 16, 7: 12, 8: 8 },
        Intermediate: { 1: 60, 2: 42, 3: 30, 4: 21, 5: 15, 6: 12, 7: 9, 8: 6 },
        Beginner: { 1: 40, 2: 28, 3: 20, 4: 14, 5: 10, 6: 8, 7: 6, 8: 4 },
      },
      lastUpdated: null,
    });
    setSuccess("");
    setError("");
  };

  const handleApplyRetroactively = async () => {
    setConfirmDialogOpen(false);

    try {
      setApplying(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("authToken");

      const response = await apiRequest("/api/tournament-settings/apply-points-retroactively", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seasonId: selectedSeason?.id, reset: true }),
      });

      const result = await response.json();
      
      let successMessage = `Successfully processed ${result.tournamentsProcessed} tournament(s) and updated ${result.playersUpdated} player(s)!`;
      
      // Add skipped reasons if any
      if (result.skippedReasons && result.skippedReasons.length > 0) {
        successMessage += `\n\nSkipped:\n${result.skippedReasons.join('\n')}`;
      }
      
      setSuccess(successMessage);
    } catch (err: any) {
      setError("Failed to apply points retroactively");
      console.error(err);
    } finally {
      setApplying(false);
    }
  };

  const handleGenerateAllStandings = async () => {
    setGenerateStandingsDialogOpen(false);

    try {
      setGeneratingStandings(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("authToken");

      const response = await apiRequest("/api/tournaments/generate-all-final-standings", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seasonId: selectedSeason?.id, force: true }),
      });

      const result = await response.json();
      
      let message = result.message + "\n\n";
      if (result.results && result.results.length > 0) {
        message += "Details:\n" + result.results.map((r: any) => 
          `${r.tournament}: ${r.status}${r.reason ? ` (${r.reason})` : ''}${r.standings ? ` - ${r.standings} positions` : ''}`
        ).join('\n');
      }
      
      setSuccess(message);
    } catch (err: any) {
      setError("Failed to generate final standings");
      console.error(err);
    } finally {
      setGeneratingStandings(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">
            🏆 Tournament Points Configuration
          </Typography>
          <SeasonSelector />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure points awarded for each position in tournaments, by level/division.
          These points are used for player rankings.
        </Typography>

        {config?.lastUpdated && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Last updated: {new Date(config.lastUpdated).toLocaleString()}
            {config.updatedBy && ` by ${config.updatedBy}`}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || applying || generatingStandings}
          >
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={resetToDefaults}
            disabled={saving || applying || generatingStandings}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="outlined"
            color="info"
            onClick={() => setGenerateStandingsDialogOpen(true)}
            disabled={saving || applying || generatingStandings}
          >
            {generatingStandings ? "Generating..." : "1. Generate Standings First"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ApplyIcon />}
            onClick={() => setConfirmDialogOpen(true)}
            disabled={saving || applying || generatingStandings}
          >
            {applying ? "Applying..." : "2. Apply Points to Tournaments"}
          </Button>
        </Box>
        
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>✨ Automatic Points Award:</strong> When you complete a tournament, points are automatically awarded to players based on this configuration!
        </Alert>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>For Existing Tournaments (Two-Step Process):</strong>
          <br />
          <strong>Step 1:</strong> Generate final standings for old completed tournaments (extracts winners from brackets)
          <br />
          <strong>Step 2:</strong> Apply points retroactively to players based on their tournament positions
          <br />
          {selectedSeason ? `Only tournaments from the selected season will be processed.` : 'All completed tournaments will be processed.'}
        </Alert>
      </Paper>

      <Grid container spacing={3}>
        {levels.map((level) => (
          <Grid item xs={12} md={6} key={level}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {level}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Position</strong></TableCell>
                        <TableCell align="right"><strong>Points</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions.map((position) => (
                        <TableRow key={position}>
                          <TableCell>
                            {position === 1 ? "🥇 1st" : 
                             position === 2 ? "🥈 2nd" : 
                             position === 3 ? "🥉 3rd" : 
                             `${position}th`}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={config?.levels[level]?.[position] || 0}
                              onChange={(e) =>
                                handlePointsChange(level, position, e.target.value)
                              }
                              inputProps={{ min: 0, style: { textAlign: "right" } }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #E49421 0%, #ff6b35 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          ⚠️ Apply Points to Previous Tournaments?
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            This will apply points to all players based on their tournament positions using the current configuration.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: "bold", color: "text.primary" }}>
            {selectedSeason 
              ? `Only tournaments from the selected season will be processed.` 
              : 'All completed tournaments will be processed.'}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Please make sure the points configuration is correct before proceeding.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyRetroactively}
            variant="contained"
            color="warning"
            disabled={applying}
            startIcon={applying ? <CircularProgress size={20} /> : <ApplyIcon />}
          >
            {applying ? "Applying..." : "Apply Points"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Standings Dialog */}
      <Dialog
        open={generateStandingsDialogOpen}
        onClose={() => setGenerateStandingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          🎯 Generate Final Standings?
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            This will generate final standings for all completed tournaments that don't have them yet. 
            This is required before applying points.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: "bold", color: "text.primary" }}>
            {selectedSeason 
              ? `Only tournaments from the selected season will be processed.` 
              : 'All completed tournaments will be processed.'}
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            Tournaments that already have final standings will be skipped. This is safe to run multiple times.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setGenerateStandingsDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateAllStandings}
            variant="contained"
            color="primary"
            disabled={generatingStandings}
            startIcon={generatingStandings ? <CircularProgress size={20} /> : null}
          >
            {generatingStandings ? "Generating..." : "Generate Standings"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TournamentPointsConfig;
